import React, { useEffect, useState } from 'react';
import { Edit2, Move } from 'lucide-react';

/**
 * Selection Overlay
 * Visual indicators for selected and hovered elements
 */
export default function SelectionOverlay({ 
  selectedElement, 
  hoveredElement,
  isVisualEditMode 
}) {
  const [selectedRect, setSelectedRect] = useState(null);
  const [hoveredRect, setHoveredRect] = useState(null);

  // Update rectangles when elements change or on scroll/resize
  useEffect(() => {
    const updateRects = () => {
      if (selectedElement?.element && selectedElement.element.getBoundingClientRect) {
        try {
          const rect = selectedElement.element.getBoundingClientRect();
          setSelectedRect({
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          });
        } catch (error) {
          console.error('Error getting selected element bounds:', error);
          setSelectedRect(null);
        }
      } else {
        setSelectedRect(null);
      }

      if (hoveredElement?.element && !selectedElement && hoveredElement.element.getBoundingClientRect) {
        try {
          const rect = hoveredElement.element.getBoundingClientRect();
          setHoveredRect({
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          });
        } catch (error) {
          console.error('Error getting hovered element bounds:', error);
          setHoveredRect(null);
        }
      } else {
        setHoveredRect(null);
      }
    };

    updateRects();

    // Add listeners for scroll (capture phase to catch inner scrolling) and resize
    window.addEventListener('scroll', updateRects, true);
    window.addEventListener('resize', updateRects);

    return () => {
      window.removeEventListener('scroll', updateRects, true);
      window.removeEventListener('resize', updateRects);
    };
  }, [selectedElement, hoveredElement]);

  if (!isVisualEditMode) return null;
  
  // Null check for tagName access
  if (!selectedElement && !hoveredElement) return null;

  return (
    <>
      {/* Hover Outline */}
      {hoveredRect && (
        <div
          className="fixed pointer-events-none z-[9998] transition-all duration-150"
          style={{
            top: `${hoveredRect.top}px`,
            left: `${hoveredRect.left}px`,
            width: `${hoveredRect.width}px`,
            height: `${hoveredRect.height}px`,
            border: '2px dashed #3b82f6',
            backgroundColor: 'rgba(59, 130, 246, 0.05)',
          }}
        >
          {/* Hover Label */}
          <div
            className="absolute -top-6 left-0 px-2 py-1 text-xs font-medium text-white rounded"
            style={{ backgroundColor: '#3b82f6' }}
          >
            {hoveredElement.tagName}
            {hoveredElement.dataType && ` • ${hoveredElement.dataType}`}
          </div>
        </div>
      )}

      {/* Selection Outline */}
      {selectedRect && (
        <div
          className="fixed pointer-events-none z-[9999] transition-all duration-150"
          style={{
            top: `${selectedRect.top}px`,
            left: `${selectedRect.left}px`,
            width: `${selectedRect.width}px`,
            height: `${selectedRect.height}px`,
            border: '2px solid #2563eb',
            backgroundColor: 'rgba(37, 99, 235, 0.1)',
            boxShadow: '0 0 0 1px rgba(37, 99, 235, 0.3)',
          }}
        >
          {/* Selection Label */}
          <div
            className="absolute -top-8 left-0 px-3 py-1.5 text-xs font-medium text-white rounded shadow-lg flex items-center gap-2"
            style={{ backgroundColor: '#2563eb' }}
          >
            <Edit2 className="w-3 h-3" />
            {selectedElement.tagName}
            {selectedElement.dataType && ` • ${selectedElement.dataType}`}
          </div>

          {/* Corner Handles (for visual feedback) */}
          <div className="absolute -top-1 -left-1 w-2 h-2 bg-blue-600 rounded-full" />
          <div className="absolute -top-1 -right-1 w-2 h-2 bg-blue-600 rounded-full" />
          <div className="absolute -bottom-1 -left-1 w-2 h-2 bg-blue-600 rounded-full" />
          <div className="absolute -bottom-1 -right-1 w-2 h-2 bg-blue-600 rounded-full" />

          {/* Spacing Indicators (optional - show padding/margin) */}
          {selectedElement?.styles && (
            <>
              {/* Top spacing indicator */}
              {parseInt(selectedElement.styles.marginTop) > 0 && (
                <div
                  className="absolute left-0 right-0 border-t border-orange-500 border-dashed"
                  style={{
                    top: `-${selectedElement.styles.marginTop}`,
                    backgroundColor: 'rgba(249, 115, 22, 0.1)',
                  }}
                >
                  <span className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-full text-xs text-orange-600 bg-white px-1 rounded">
                    {selectedElement.styles.marginTop}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}

